export type ExportFormat = "pdf" | "excel";

type Cell = string | number;

/** Coordonnées de l'en-tête des PDF : mêmes valeurs que safecheck.identity côté serveur. */
const IDENTITY = {
  name: "SAFECHECK RDC SA",
  lines: ["20, Avenue de la caisse d'Épargne", "9ème niveau, Immeuble Cadeco", "Kinshasa – Gombe", "info@safecheck-rdc.com · www.safecheck-rdc.com"],
};

/** Logo pour le PDF ; sans lui, le document reste exportable. */
async function loadLogo(): Promise<{ data: string; ratio: number } | null> {
  try {
    const blob = await (await fetch("/logo-fonce.png")).blob();
    const data = await new Promise<string>((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = () => resolve(String(reader.result));
      reader.onerror = reject;
      reader.readAsDataURL(blob);
    });
    const size = await createImageBitmap(blob);
    return { data, ratio: size.width / size.height };
  } catch {
    return null;
  }
}

function baseName(filename: string): string {
  return filename.replace(/\.[a-z0-9]+$/i, "");
}

/** Titre du document : le titre de la page affichée, sinon le nom du fichier. */
function documentTitle(filename: string): string {
  const heading = document.querySelector("main h1")?.textContent?.trim();
  return heading || baseName(filename).replace(/[-_]/g, " ");
}

async function exportPdf(filename: string, headers: string[], rows: Cell[][], title?: string) {
  // Chargées à la demande : ces bibliothèques ne pèsent pas sur l'ouverture de l'application.
  const [{ jsPDF }, { default: autoTable }] = await Promise.all([import("jspdf"), import("jspdf-autotable")]);
  const pdf = new jsPDF({ orientation: headers.length > 5 ? "landscape" : "portrait", unit: "pt", format: "a4" });
  const heading = title ?? documentTitle(filename);
  const pageWidth = pdf.internal.pageSize.getWidth();

  const logo = await loadLogo();
  if (logo) pdf.addImage(logo.data, "PNG", 40, 22, 44 * logo.ratio, 44, undefined, "FAST");
  pdf.setFontSize(8);
  pdf.setTextColor(20, 35, 42);
  pdf.text(IDENTITY.name, pageWidth - 40, 28, { align: "right" });
  pdf.setTextColor(90);
  IDENTITY.lines.forEach((line, index) => pdf.text(line, pageWidth - 40, 39 + index * 10, { align: "right" }));
  pdf.setDrawColor(22, 58, 43);
  pdf.line(40, 76, pageWidth - 40, 76);

  pdf.setFontSize(14);
  pdf.setTextColor(0);
  pdf.text(heading, 40, 96);
  pdf.setFontSize(9);
  pdf.setTextColor(110);
  pdf.text(`Édité le ${new Date().toLocaleString("fr-FR")} · ${rows.length} ligne(s)`, 40, 112);

  autoTable(pdf, {
    startY: 124,
    head: [headers],
    body: rows.map((row) => row.map(String)),
    styles: { fontSize: 8, cellPadding: 4, overflow: "linebreak" },
    headStyles: { fillColor: [22, 58, 43], textColor: 255 },
    alternateRowStyles: { fillColor: [246, 250, 238] },
    margin: { left: 40, right: 40, bottom: 40 },
    didDrawPage: (data) => {
      const { width, height } = pdf.internal.pageSize;
      pdf.setFontSize(8);
      pdf.setTextColor(110);
      pdf.text(`SafeCheck · page ${data.pageNumber}`, width - 40, height - 20, { align: "right" });
    },
  });
  pdf.save(`${baseName(filename)}.pdf`);
}

async function exportExcel(filename: string, headers: string[], rows: Cell[][]) {
  const { default: writeXlsxFile } = await import("write-excel-file");
  const asCell = (value: Cell) => (typeof value === "number" ? value : String(value ?? ""));
  const data = [
    headers.map((value) => ({ value, fontWeight: "bold" as const })),
    ...rows.map((row) => row.map((value) => ({ value: asCell(value) }))),
  ];
  const widths = headers.map((header, index) => ({
    width: Math.min(45, Math.max(10, header.length + 2, ...rows.map((row) => String(row[index] ?? "").length + 2))),
  }));
  await writeXlsxFile(data, { columns: widths, fileName: `${baseName(filename)}.xlsx` });
}

export function exportDocument(format: "pdf" | "excel", filename: string, headers: string[], rows: Cell[][], title?: string): Promise<void> {
  return format === "pdf" ? exportPdf(filename, headers, rows, title) : exportExcel(filename, headers, rows);
}
